import axios from 'axios';
import { LOCALE_STORAGE_KEY } from '../constants/locale';
import { toAcceptLanguage } from '../i18n/acceptLanguage';
import {
  SERVICE_DISABLED_CODE,
  SERVICE_DISABLED_MESSAGE,
  disabledServiceForUrl,
} from '../constants/serviceGate';

export class ApiError extends Error {
  /**
   * @param {string} message
   * @param {number|string|null|undefined} code
   * @param {object|null|undefined} raw
   * @param {{ httpStatus?: number|null, networkCode?: string|null }} [extras]
   */
  constructor(message, code, raw, extras = {}) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.raw = raw;
    this.httpStatus = extras.httpStatus ?? null;
    this.networkCode = extras.networkCode ?? null;
  }
}

const TOKEN_EXPIRED_CODE = 90005;

function isTokenExpired(payload) {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const key = String(payload.key || '').toLowerCase();
  return payload.code === TOKEN_EXPIRED_CODE || key === 'token.expired';
}

/**
 * Dispatch auth-expired before clearing session so listeners can capture memberId
 * for private offline cache wipe (AUTH57).
 */
function handleTokenExpired(clearSession, expiredEvent) {
  if (expiredEvent) {
    window.dispatchEvent(new Event(expiredEvent));
  }
  clearSession();
}

/**
 * @param {object} options
 * @param {{ TOKEN: string, USER: string, PERMISSIONS: string }} options.storageKeys
 * @param {() => void} options.clearSession
 * @param {string} [options.expiredEvent] token 過期時派發的事件名
 */
export function createApiClient({ storageKeys, clearSession, expiredEvent }) {
  const client = axios.create({
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    // 已停用的服務：送出前直接拒絕（fail-closed，不發出網路請求）
    const disabledService = disabledServiceForUrl(config.url);
    if (disabledService) {
      return Promise.reject(
        new ApiError(SERVICE_DISABLED_MESSAGE, SERVICE_DISABLED_CODE, { service: disabledService }, {
          httpStatus: null,
          networkCode: SERVICE_DISABLED_CODE,
        })
      );
    }
    const token = localStorage.getItem(storageKeys.TOKEN);
    if (token) {
      config.headers.Authorization = token;
    }
    const acceptLanguage = toAcceptLanguage(localStorage.getItem(LOCALE_STORAGE_KEY));
    if (acceptLanguage) {
      config.headers['Accept-Language'] = acceptLanguage;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const body = response.data;
      if (!body || typeof body.code !== 'number') {
        return response.data;
      }
      if (body.code !== 200) {
        if (isTokenExpired(body)) {
          handleTokenExpired(clearSession, expiredEvent);
        }
        throw new ApiError(body.message || '請求失敗', body.code, body, {
          httpStatus: response.status ?? null,
        });
      }
      return body.data;
    },
    (error) => {
      if (error instanceof ApiError) {
        return Promise.reject(error);
      }

      const payload = error.response?.data;
      const httpStatus = error.response?.status ?? null;
      const networkCode = typeof error.code === 'string' ? error.code : null;

      if (isTokenExpired(payload)) {
        handleTokenExpired(clearSession, expiredEvent);
      }

      const message =
        payload?.message ||
        payload?.mes ||
        error.message ||
        '網路請求失敗';

      return Promise.reject(new ApiError(message, payload?.code, payload, {
        httpStatus,
        networkCode,
      }));
    }
  );

  /**
   * 非 JSON 回應（例如 responseType: 'blob'）的錯誤由呼叫端解析成物件後，
   * 以與上方 JSON 回應相同的規則判斷 token 過期，並沿用同一個 clearSession／expiredEvent。
   * 只在仍有登入 token 時通知一次：同時多個請求失敗不會重複派發。
   * @param {object|null|undefined} payload
   * @returns {boolean} payload 是否為 token 過期
   */
  client.reportParsedErrorPayload = (payload) => {
    if (!isTokenExpired(payload)) {
      return false;
    }
    if (localStorage.getItem(storageKeys.TOKEN)) {
      handleTokenExpired(clearSession, expiredEvent);
    }
    return true;
  };

  return client;
}
